import { Form } from "@unform/web";
import { CheckCheck, CheckCircle, User } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries, handleUpdatedFields } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { format, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSearchParams } from "react-router-dom";
import { Scope } from "@unform/core";
import FileInputV3 from "~/components/RegisterForm/FileInputV3";
import { AlertContext } from "~/App";
import SignaturePad from "react-signature-pad-wrapper";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { app } from "~/services/firebase";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
export const InputContext = createContext({});

export default function TransferOutside({ access = null, handleOpened }) {
  const [searchparams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState({
    enrollmentemergencies: [
      {
        name: "",
        relationship_type: "",
        email: "",
        phone: "",
      },
    ],
    activeMenu: null,
    lastActiveMenu: null,
    loaded: false,
  });
  const [formType, setFormType] = useState("full");
  const [fullscreen, setFullscreen] = useState(true);
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(true);
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const fileEnrollmentRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const id = searchparams.get("crypt");
  const { alertBox } = useContext(AlertContext);
  const signatureRef = useRef();

  const getMenuByName = (name) =>
    menus.find((menu) => menu.name === name) || null;

  const normalizeLastActiveMenu = (value, fallbackName) => {
    const name =
      (value && typeof value === "object" && value.name) ||
      (typeof value === "string" && value) ||
      fallbackName ||
      menus[0].name;
    const match = getMenuByName(name);
    return {
      name,
      order: match?.order || null,
    };
  };

  const menus = [
    { order: 1, name: "transfer-request" },
    { order: 2, name: "transfer-dso" },
    { order: 3, name: "finished" },
  ];

  useEffect(() => {
    async function getDocuments(type = "") {
      const { data } = await api.get(
        `/documentsByOrigin?origin=Transfer Eligibility&type=${type}&subtype=Student`
      );
      return data;
    }
    async function getPageData() {
      if (id !== "new") {
        try {
          let documents = [];
          const { data } = await api.get(`/outside/enrollments/${id}`);
          const { data: filialsData } = await api.get(`/filials`);

          documents = await getDocuments(data.students.processsubstatuses.name);
          const filialOptions = filialsData.rows
            ?.filter((filial) => filial.id !== pageData.filial_id)
            ?.map((filial) => {
              return {
                value: filial.id,
                label: filial.id ? "MILA - " + filial.name : filial.name,
                isDisabled: true,
              };
            });
          filialOptions.push({ value: null, label: "Other School" });
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
          const hasStudentSignature = !!data.enrollmenttransfers?.student_signature;
          const validMenuNames = menus.map((m) => m.name);
          const candidateMenu = searchparams.has("activeMenu")
            ? searchparams.get("activeMenu")
            : data.transfer_step || data.form_step;
          const resolvedActiveMenu = hasStudentSignature
            ? "finished"
            : validMenuNames.includes(candidateMenu)
            ? candidateMenu
            : menus[0].name;
          const resolvedLastMenu = normalizeLastActiveMenu(
            resolvedActiveMenu,
            resolvedActiveMenu
          );
          setPageData({
            ...data,
            documents,
            filials: filialOptions,
            loaded: true,
            activeMenu: resolvedActiveMenu,
            lastActiveMenu: resolvedLastMenu,
          });
        } catch (err) {
          if (err.response && err.response.data && err.response.data.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
          console.log(err);
        }
      }
    }
    if (!pageData.loaded) {
      getPageData();
    }
  }, [pageData.loaded]);

  async function handleGeneralFormSubmit(data) {
    setLoading(true);

    try {
      const hasNoSignatureYet = !pageData.enrollmenttransfers?.student_signature;

      if (successfullyUpdated && !hasNoSignatureYet) {
        toast("No need to be saved!", {
          autoClose: 1000,
          type: "info",
          transition: Zoom,
        });
        setLoading(false);
        return;
      }

      if (pageData.activeMenu === "transfer-request" && id !== "new") {
        const signaturePad = signatureRef.current;
        const isEmptySignature =
          hasNoSignatureYet &&
          (!signaturePad ||
            !signaturePad.instance ||
            (signaturePad.instance.isEmpty && signaturePad.instance.isEmpty()));

        if (isEmptySignature) {
          toast("Please add your signature before saving.", {
            autoClose: 2000,
            type: "error",
            transition: Zoom,
          });
          setLoading(false);
          return;
        }

        // 1. Validate required documents
        const missingRequired = (pageData.documents || []).filter((doc) => {
          if (doc.active === false) return false;
          if (!doc.required) return false;
          const hasSaved = (pageData.enrollmentdocuments || []).some((ed) => ed.document_id === doc.id);
          const hasPending = fileEnrollmentRefs.current.some((r, i) => {
            const d = (pageData.documents || [])[i];
            return d?.id === doc.id && r?.hasPending();
          });
          return !hasSaved && !hasPending;
        });
        if (missingRequired.length > 0) {
          const names = missingRequired.map((d) => d.title).join(", ");
          toast(`The following required documents are missing: ${names}`, { type: "error", autoClose: 5000 });
          setLoading(false);
          return;
        }

        // 2. Upload pending files
        const refsWithPending = fileEnrollmentRefs.current.filter((r) => r?.hasPending());
        if (refsWithPending.length > 0) {
          const toastId = toast.loading("Files are being uploaded...");
          try {
            for (const r of refsWithPending) {
              await r.uploadPending();
            }
            toast.update(toastId, { render: "All files have been uploaded!", type: "success", autoClose: 3000, isLoading: false });
          } catch (err) {
            toast.update(toastId, { render: err?.response?.data?.error || "Upload failed.", type: "error", autoClose: 3000, isLoading: false });
            setLoading(false);
            return;
          }
        }

        // 3. Save form fields
        const updated = handleUpdatedFields(data, pageData);
        const objUpdated = Object.fromEntries(updated);
        const { date_of_birth, passport_expiration_date, i94_expiration_date } = objUpdated;
        delete objUpdated.documents;

        try {
          await api.put(`/outside/enrollments/${id}`, {
            ...objUpdated,
            activeMenu: pageData.activeMenu,
            lastActiveMenu: normalizeLastActiveMenu(pageData.lastActiveMenu, pageData.activeMenu),
            date_of_birth: date_of_birth ? format(date_of_birth, "yyyy-MM-dd") : null,
            passport_expiration_date: passport_expiration_date ? format(passport_expiration_date, "yyyy-MM-dd") : null,
            i94_expiration_date: i94_expiration_date ? format(i94_expiration_date, "yyyy-MM-dd") : null,
          });
        } catch (err) {
          toast(err?.response?.data?.error || "Save failed.", { type: "error", autoClose: 3000 });
          setLoading(false);
          return;
        }

        // 4. Upload signature (last step)
        if (hasNoSignatureYet && signaturePad) {
          const signature = signaturePad.toDataURL();
          try {
            const fileUuid = uuidv4();
            const storage = getStorage(app);
            const local = "Enrollments/Signatures/" + fileUuid + ".png";
            const imageRef = ref(storage, local);
            const snapshot = await uploadString(imageRef, signature.substring(22), "base64");
            const downloadURL = await getDownloadURL(snapshot.ref);
            await api.post(`/enrollmenttransfer/student_signature`, {
              enrollment_id: id,
              files: {
                url: downloadURL,
                name: fileUuid + ".png",
                size: signature.length,
                key: fileUuid + ".png",
              },
            });
          } catch (err) {
            toast(err?.response?.data?.error || "Failed to upload signature.", { type: "error", autoClose: 3000 });
            setLoading(false);
            return;
          }
        }

        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        setTimeout(() => window.location.reload(), 1000);
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleInactivate() {}

  function handleDeleteDocument(id) {
    // const { file } = pageData.staffdocuments.find(staffdocument => staffdocument.id === id);
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/enrollmentdocuments/${id}`);
              toast("File deleted!", { autoClose: 1000 });
              setPageData({
                ...pageData,
                enrollmentdocuments: pageData.enrollmentdocuments.filter(
                  (enrollmentdocument) => enrollmentdocument.id !== id
                ),
              });
            } catch (err) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  function handleClearSignature() {
    const signaturePad = signatureRef.current;

    if (signaturePad) {
      signaturePad.instance.clear();
      setSuccessfullyUpdated(true);
    }
  }

  function handlePreviousSchoolFilial(el) {
    setPageData({
      ...pageData,
      enrollmenttransfers: {
        ...pageData.enrollmenttransfers,
        previous_school_id: el?.value ?? null,
      },
    });
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {!sent && pageData.loaded ? (
        <div className="flex h-full flex-col items-start justify-between gap-4 md:flex-row">
          <div className="flex flex-row items-center justify-between text-xs w-32 gap-4 md:flex-col">
            <RegisterFormMenu
              disabled={false}
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: menus[0].name })
              }
              activeMenu={pageData.activeMenu}
              name="transfer-request"
            >
              <User size={22} /> Transfer Information
            </RegisterFormMenu>
            <RegisterFormMenu
              disabled={!pageData.enrollmenttransfers?.student_signature}
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: "finished" })
              }
              activeMenu={pageData.activeMenu}
              name="finished"
            >
              <CheckCheck size={18} /> Finished
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                handleInactivate,
                handleOutsideMail: null,
                canceled: pageData.canceled_at,
              }}
            >
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll h-full">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <FormHeader
                    saveText="Save & Continue"
                    outside={!searchparams.has("activeMenu")}
                    loading={loading}
                    access={{
                      view: true,
                      edit: true,
                      create: true,
                      inactivate: true,
                    }}
                    title={
                      pageData.students.name +
                      " " +
                      pageData.students.last_name +
                      " - Enrollment Process - Transfer Eligibility"
                    }
                    registry={registry}
                    InputContext={InputContext}
                  />
                  {pageData.loaded ? (
                    <>
                      {pageData.activeMenu === "transfer-request" && (
                        <InputLineGroup
                          title="Student Information"
                          activeMenu={
                            pageData.activeMenu === "transfer-request"
                          }
                        >
                          <Scope path={`students`}>
                            <InputLine title="Student Information">
                              <Input
                                type="text"
                                readOnly
                                name="name"
                                required
                                grow
                                title="First Name"
                                defaultValue={pageData.students.name}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                readOnly
                                name="last_name"
                                required
                                grow
                                title="Last Name"
                                defaultValue={pageData.students.last_name}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                readOnly
                                name="email"
                                required
                                grow
                                title="E-mail"
                                defaultValue={pageData.students.email}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="nsevis"
                                maxLength={11}
                                minLength={11}
                                required
                                grow
                                title="NSEVIS"
                                defaultValue={pageData.students.nsevis}
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </Scope>
                          <InputLine title="Previous School">
                            <Scope path={`enrollmenttransfers`}>
                              <SelectPopover
                                onChange={(el) =>
                                  handlePreviousSchoolFilial(el)
                                }
                                name="previous_school_id"
                                required
                                grow
                                title="Previous School"
                                options={pageData.filials}
                                defaultValue={
                                  pageData.enrollmenttransfers
                                    ? pageData.filials.find(
                                        (filial) =>
                                          filial.value ===
                                          pageData.enrollmenttransfers
                                            .previous_school_id
                                      )
                                    : null
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="previous_school_name"
                                required
                                grow
                                title="School Name"
                                defaultValue={
                                  pageData.enrollmenttransfers
                                    ? pageData.enrollmenttransfers
                                        .previous_school_name
                                    : null
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="previous_school_dso_name"
                                required
                                grow
                                title="DSO Name"
                                defaultValue={
                                  pageData.enrollmenttransfers
                                    ? pageData.enrollmenttransfers
                                        .previous_school_dso_name
                                    : null
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="previous_school_dso_email"
                                required
                                grow
                                title="DSO Email"
                                defaultValue={
                                  pageData.enrollmenttransfers
                                    ? pageData.enrollmenttransfers
                                        .previous_school_dso_email
                                    : null
                                }
                                InputContext={InputContext}
                              />
                            </Scope>
                          </InputLine>
                          {pageData.documents &&
                            pageData.documents.length > 0 &&
                            [...pageData.documents]
                              .filter((doc) => doc.active !== false)
                              .sort((a, b) => {
                                const numA = parseInt(a.title);
                                const numB = parseInt(b.title);
                                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                if (!isNaN(numA)) return -1;
                                if (!isNaN(numB)) return 1;
                                return 0;
                              })
                              .map((document, index) => (
                                <FileInputV3
                                  key={index}
                                  ref={(el) => (fileEnrollmentRefs.current[index] = el)}
                                  document={document}
                                  enrollmentdocuments={pageData.enrollmentdocuments}
                                  enrollmentId={id}
                                  isEditable={true}
                                  onDelete={handleDeleteDocument}
                                  onUploaded={() => setPageData((prev) => ({ ...prev, loaded: false }))}
                                  onPendingChange={() => setSuccessfullyUpdated(false)}
                                />
                              ))}
                          {!pageData.enrollmenttransfers?.student_signature ? (
                            <InputLine
                              title={
                                <>
                                  Student Signature{" "}
                                  <span className="text-mila_orange">*</span>
                                </>
                              }
                            >
                              <div className="flex w-full md:w-auto flex-col items-start justify-start min-w-0">
                                <div
                                  onClick={() => setSuccessfullyUpdated(false)}
                                  className="w-full md:w-auto border rounded-sm"
                                  style={{ height: window.innerWidth < 768 ? 160 : 240 }}
                                >
                                  <SignaturePad
                                    redrawOnResize
                                    ref={signatureRef}
                                    width={window.innerWidth < 768 ? undefined : 600}
                                    height={window.innerWidth < 768 ? 160 : 240}
                                    options={{
                                      backgroundColor: "#FFF",
                                      penColor: "#010000ff",
                                    }}
                                  />
                                </div>
                                <div className="flex flex-row items-center justify-start gap-2">
                                  <button
                                    type="button"
                                    onClick={handleClearSignature}
                                    className="bg-primary text-white rounded-md my-2 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
                                  >
                                    Clear Signature
                                  </button>
                                </div>
                              </div>
                            </InputLine>
                          ) : (
                            <InputLine
                              title={
                                <>
                                  Student Signature{" "}
                                  <span className="text-mila_orange">*</span>
                                </>
                              }
                            >
                              <div className="flex w-full md:w-auto flex-col items-start justify-start min-w-0">
                                <img
                                  src={
                                    pageData.enrollmenttransfers
                                      ?.studentsignature?.url
                                  }
                                  className="border w-full md:w-96"
                                />
                                <p className="text-xs p-2 border border-t-0 rounded-b-md bg-slate-100 w-full md:w-96">
                                  Signed:{" "}
                                  {format(
                                    parseISO(
                                      pageData.enrollmenttransfers
                                        ?.studentsignature?.created_at
                                    ),
                                    "MMM do, yyyy"
                                  )}{" "}
                                  at{" "}
                                  {format(
                                    parseISO(
                                      pageData.enrollmenttransfers
                                        ?.studentsignature?.created_at
                                    ),
                                    "HH:mm"
                                  )}
                                </p>
                              </div>
                            </InputLine>
                          )}
                        </InputLineGroup>
                      )}
                      <InputLineGroup
                        title="Finish"
                        activeMenu={pageData.activeMenu === "finished"}
                      >
                        <div className="flex flex-1 w-full flex-col items-center justify-center text-center gap-4">
                          <div className="flex w-full flex-row items-center justify-center text-center gap-4">
                            <CheckCircle size={32} color="#00b361" />
                            <span className="text-lg font-bold">
                              Your form has been successfully submitted.
                            </span>
                          </div>
                        </div>
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
